import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle, XCircle, Send, RefreshCw, Search } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const selectClass = "w-full h-9 rounded-md border border-input bg-transparent px-3 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring";

const IcertiDialog = ({ appointment, open, onClose, onChanged }) => {
  const [form, setForm] = useState(null);
  const [options, setOptions] = useState(null);
  const [files, setFiles] = useState([]);
  const [pedido, setPedido] = useState(null);
  const [result, setResult] = useState(null);
  const [lastValidatedForm, setLastValidatedForm] = useState(null);
  const [busy, setBusy] = useState("");
  const [catastroHint, setCatastroHint] = useState("");

  useEffect(() => {
    if (!open || !appointment) return;
    setForm(null); setResult(null); setLastValidatedForm(null); setCatastroHint("");
    (async () => {
      try {
        const [opt, data] = await Promise.all([
          axios.get(`${API}/icerti/options`),
          axios.get(`${API}/icerti/appointments/${appointment.id}`),
        ]);
        setOptions(opt.data);
        setForm({ ...data.data.form, refrigeracion: data.data.form.refrigeracion || "No hay" });
        setFiles(data.data.files);
        setPedido(data.data.pedido);
      } catch {
        toast.error("No se pudo cargar los datos de iCerti");
        onClose();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, appointment?.id]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const dirty = !result || JSON.stringify(form) !== lastValidatedForm;
  const total = result?.precio?.total_con_iva ?? result?.precio?.total;

  const verify = async () => {
    setBusy("verify");
    try {
      const res = await axios.post(`${API}/icerti/appointments/${appointment.id}/validate`, { form });
      setResult(res.data);
      setLastValidatedForm(JSON.stringify(form));
      const fresh = await axios.get(`${API}/icerti/appointments/${appointment.id}`);
      setFiles(fresh.data.files);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al verificar con iCerti");
    } finally { setBusy(""); }
  };

  const send = async () => {
    const price = total != null ? ` (${Number(total).toFixed(2)} €)` : "";
    if (!window.confirm(`¿Enviar este pedido a iCerti${price}?\n\nSe creará un pedido real.`)) return;
    setBusy("send");
    try {
      const res = await axios.post(`${API}/icerti/appointments/${appointment.id}/send`, { confirm: true });
      setPedido(res.data.pedido);
      toast.success("Pedido enviado a iCerti");
      onChanged?.(res.data.pedido);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al enviar a iCerti");
    } finally { setBusy(""); }
  };

  const fillFromCatastro = async () => {
    setBusy("catastro");
    setCatastroHint("");
    try {
      const res = await axios.get(`${API}/icerti/catastro`, { params: { ref: form.ref_catastral } });
      const d = res.data;
      // Only fills this window's fields; the appointment itself is never touched.
      const knownCcaa = options.ccaa.some((c) => c.value === d.ccaa);
      setForm((f) => ({
        ...f,
        domicilio: d.domicilio || f.domicilio,
        municipio: d.municipio || f.municipio,
        provincia: d.provincia || f.provincia,
        ccaa: knownCcaa ? d.ccaa : f.ccaa,
      }));
      setCatastroHint(`${d.fuente}${d.superficie_construida ? ` · superficie construida ${d.superficie_construida} m²` : ""}${knownCcaa ? "" : " · elige la comunidad a mano"}`);
      toast.success("Datos del catastro cargados");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo consultar el catastro");
    } finally { setBusy(""); }
  };

  const refresh = async () => {
    setBusy("refresh");
    try {
      const res = await axios.post(`${API}/icerti/appointments/${appointment.id}/refresh`);
      setPedido(res.data.pedido);
      onChanged?.();
    } catch {
      toast.error("No se pudo actualizar el estado");
    } finally { setBusy(""); }
  };

  const field = (label, key, props = {}) => (
    <div>
      <Label>{label}</Label>
      <Input value={form[key] ?? ""} onChange={set(key)} disabled={!!pedido} {...props} />
    </div>
  );

  const pick = (label, key, values) => (
    <div>
      <Label>{label}</Label>
      {values.length > 0 ? (
        <select className={selectClass} value={form[key] ?? ""} onChange={set(key)} disabled={!!pedido}>
          <option value="">Seleccionar…</option>
          {values.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
      ) : (
        <Input value={form[key] ?? ""} onChange={set(key)} disabled={!!pedido} />
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto" data-testid="icerti-dialog">
        <DialogHeader>
          <DialogTitle>Enviar a iCerti</DialogTitle>
          <DialogDescription>
            RES022 · Trámite completo CAE: No · Ventilado: No · Producto Partner CAE | Aisla Solar
          </DialogDescription>
        </DialogHeader>

        {!form || !options ? (
          <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div>
        ) : (
          <div className="space-y-4">
            {pedido && (
              <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm flex items-center justify-between" data-testid="icerti-sent">
                <span className="text-emerald-800">
                  <CheckCircle size={16} className="inline mr-1" />
                  Enviado a iCerti · pedido{" "}
                  <a href={`https://icerti.es/mi-cuenta/view-order/${pedido.pedido_id}/`} target="_blank" rel="noopener noreferrer" className="underline hover:text-emerald-900">
                    {pedido.numero || pedido.pedido_id}
                  </a>{" "}
                  · expediente {pedido.expediente || "—"} · estado: <strong>{pedido.estado || "—"}</strong>
                </span>
                <Button variant="outline" size="sm" onClick={refresh} disabled={busy === "refresh"}>
                  {busy === "refresh" ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                </Button>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              {field("Nombre y apellidos", "nombre")}
              {field("DNI / NIE", "dni")}
              {field("Teléfono", "telefono")}
              <div>
                {field("Referencia catastral", "ref_catastral")}
                {!pedido && (
                  <div className="mt-1 flex items-center gap-2">
                    <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={fillFromCatastro} disabled={!!busy} data-testid="icerti-catastro">
                      {busy === "catastro" ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Search size={12} className="mr-1" />}
                      Rellenar desde catastro
                    </Button>
                    {catastroHint && <span className="text-xs text-slate-500">{catastroHint}</span>}
                  </div>
                )}
              </div>
              <div className="col-span-2">{field("Domicilio", "domicilio")}</div>
              {field("Municipio", "municipio")}
              {field("Provincia", "provincia")}
              <div>
                <Label>Comunidad autónoma (tasa)</Label>
                <select className={selectClass} value={form.ccaa ?? ""} onChange={set("ccaa")} disabled={!!pedido} data-testid="icerti-ccaa">
                  <option value="">Seleccionar…</option>
                  {options.ccaa.map((c) => <option key={c.value} value={c.value}>{c.value} — {c.tasa.toFixed(2)} €</option>)}
                </select>
              </div>
              {pick("Tipo de inmueble", "tipo_inmueble2", options.tipo_inmueble2)}
            </div>

            <div className="grid grid-cols-2 gap-3">
              {field("Superficie aislada (m²)", "superficie", { inputMode: "decimal" })}
              {field("Fecha de instalación", "fecha_instalacion", { type: "date" })}
              {field("Aislante", "aislante")}
              {field("Espesor (mm)", "espesor", { inputMode: "numeric" })}
              {pick("Zona de intervención", "zona", options.zona)}
              {field("Fotovoltaica (kWp) — vacío = No", "fotovoltaica", { placeholder: "Ej: 4.5" })}
            </div>

            <div className="grid grid-cols-1 gap-3">
              {pick("Agua caliente sanitaria", "acs", options.acs)}
              {pick("Calefacción", "calefaccion", options.calefaccion)}
              {pick("Refrigeración", "refrigeracion", options.refrigeracion)}
            </div>

            <div>
              <Label>Comentarios (opcional)</Label>
              <Textarea value={form.comentarios ?? ""} onChange={set("comentarios")} disabled={!!pedido} rows={2} />
            </div>

            <div className="p-3 rounded-lg border border-slate-300 bg-slate-50">
              <p className="text-xs font-semibold text-slate-500 mb-2">ARCHIVOS QUE SE ENVIARÁN</p>
              <ul className="text-sm space-y-1">
                {files.map((f, i) => (
                  <li key={i} className="flex items-center gap-2">
                    {f.present
                      ? <CheckCircle size={14} className="text-emerald-600" />
                      : <XCircle size={14} className={f.required ? "text-red-500" : "text-amber-500"} />}
                    <span className={f.present ? "" : f.required ? "text-red-600" : "text-amber-600"}>
                      {f.label}{!f.present && " — falta"}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {result && (
              <div className={`p-3 rounded-lg border text-sm ${result.ok && !dirty ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-700"}`} data-testid="icerti-result">
                {result.stage === "form" && (
                  <p>Faltan datos: <strong>{result.missing_form.join(", ")}</strong></p>
                )}
                {result.stage === "iCerti" && (
                  <>
                    <p className="font-semibold">
                      {result.ok ? "Verificación correcta" : `Verificación: ${result.validacion || "con problemas"}`}
                      {total != null && ` · Total ${Number(total).toFixed(2)} €${result.precio?.iva_estimado != null ? ` (IVA 21 % ${Number(result.precio.iva_estimado).toFixed(2)} € + tasa ${Number(result.precio?.tasas || 0).toFixed(2)} €)` : ` (tasa ${Number(result.precio?.tasas || 0).toFixed(2)} €)`}`}
                    </p>
                    {result.errores?.map((e, i) => <p key={i}>• {e.mensaje || JSON.stringify(e)}</p>)}
                    {result.warnings?.map((w, i) => <p key={i}>• {typeof w === "string" ? w : JSON.stringify(w)}</p>)}
                    {result.duplicado && <p>• iCerti ya tiene un pedido con este expediente</p>}
                    {result.missing_files?.length > 0 && <p>• Faltan archivos: {result.missing_files.join(", ")}</p>}
                  </>
                )}
                {result.ok && dirty && <p className="mt-1">Has cambiado datos: verifica de nuevo antes de enviar.</p>}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
          {!pedido && form && (
            <>
              <Button variant="outline" onClick={verify} disabled={!!busy} data-testid="icerti-verify">
                {busy === "verify" ? <Loader2 size={14} className="mr-2 animate-spin" /> : <CheckCircle size={14} className="mr-2" />}
                Verificar
              </Button>
              <Button onClick={send} disabled={!!busy || !result?.ok || dirty} className="bg-blue-600 hover:bg-blue-700" data-testid="icerti-send">
                {busy === "send" ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Send size={14} className="mr-2" />}
                Enviar a iCerti
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default IcertiDialog;
